/**
 * EPUB structure parsing utilities.
 * Handles container.xml, OPF manifest/spine, and TOC (nav.xhtml / toc.ncx).
 */

/** Parse an XML string into a Document. */
export function parseXML(text) {
  return new DOMParser().parseFromString(text, 'text/xml');
}

/**
 * Resolve a relative path against a base path.
 * Handles `..`, `.`, absolute `/`, and URL-encoded segments.
 */
export function resolvePath(basePath, relativePath) {
  if (relativePath.startsWith('/')) return relativePath.substring(1);

  const baseDir = basePath.substring(0, basePath.lastIndexOf('/') + 1);
  const parts = (baseDir + relativePath).split('/');
  const resolved = [];

  for (const part of parts) {
    if (part === '..') {
      if (resolved.length > 0) resolved.pop();
    } else if (part !== '.' && part !== '') {
      resolved.push(part);
    }
  }
  return resolved.join('/');
}

/** Extract the filename portion of an href (strip directory and fragment). */
export function hrefBasename(href) {
  if (!href) return '';
  const base = href.split('#')[0];
  return base.split('/').filter(Boolean).pop() || base;
}

/** Loose comparison of two EPUB hrefs (handles different base paths). */
export function hrefMatches(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  const ab = hrefBasename(a);
  const bb = hrefBasename(b);
  if (ab === bb) return true;
  return a.endsWith(b) || b.endsWith(a);
}

/**
 * Map an original-spine href to an effective-spine index.
 *
 * getCurrentLocation() stores DOM-level hrefs (original spine), but the
 * effective (post-merge) spine may carry a different representative href
 * for merged chapters (all front matter, multi-file body chapters).
 *
 * Fast path: direct match in effective spine.
 * Slow path: search full spine → map via frontMatterEnd / bodyStarts.
 */
export function findEffectiveChapterIdx(chapterHref, effectiveSpine, fullSpine, frontMatterEnd, bodyStarts) {
  if (!chapterHref || !effectiveSpine?.length) return -1;

  // Fast path — works for non-merged & first-item-of-group chapters
  const directIdx = effectiveSpine.findIndex((item) => hrefMatches(item.href, chapterHref));
  if (directIdx >= 0) return directIdx;

  // Slow path — href belongs to a merged group whose representative differs
  if (!fullSpine?.length) return -1;
  const fullIdx = fullSpine.findIndex((item) => hrefMatches(item.href, chapterHref));
  if (fullIdx < 0) return -1;

  const hasFrontMatter = frontMatterEnd > 0;

  // Front matter (spine[0 .. frontMatterEnd-1]) → effective index 0
  if (hasFrontMatter && fullIdx < frontMatterEnd) return 0;

  // Body chapters: find which bodyStarts group contains fullIdx
  if (bodyStarts?.length > 0) {
    for (let i = 0; i < bodyStarts.length; i++) {
      const start = bodyStarts[i];
      const end = bodyStarts[i + 1] ?? fullSpine.length;
      if (fullIdx >= start && fullIdx < end) {
        return (hasFrontMatter ? 1 : 0) + i;
      }
    }
  }

  // Fallback: no bodyStarts available — map via offset from front matter
  if (hasFrontMatter) return fullIdx - frontMatterEnd + 1;
  return fullIdx;
}

/** Parse OPF to extract manifest, spine, and Dublin Core metadata. */
export async function parseOPF(opfText) {
  const opfDoc = parseXML(opfText);
  const manifest = {};
  const spine = [];

  opfDoc.querySelectorAll('manifest > item').forEach((item) => {
    const id = item.getAttribute('id');
    const href = item.getAttribute('href');
    const mediaType = item.getAttribute('media-type');
    manifest[id] = { href, mediaType };
  });

  opfDoc.querySelectorAll('spine > itemref').forEach((item) => {
    const idref = item.getAttribute('idref');
    if (manifest[idref]) spine.push(manifest[idref]);
  });

  const titleEl = opfDoc.querySelector('metadata title, dc\\:title');
  const creatorEl = opfDoc.querySelector('metadata creator, dc\\:creator');
  const metadata = {
    title: titleEl?.textContent?.trim() || null,
    author: creatorEl?.textContent?.trim() || null,
  };

  return { manifest, spine, metadata };
}

// ─── TOC Parsing ─────────────────────────────────────────────────────────────

/** Parse EPUB 3 nav.xhtml <nav epub:type="toc">. */
function parseNavXHTML(navText) {
  const navDoc = parseXML(navText);
  const toc = [];

  const navElement =
    navDoc.querySelector('nav[epub\\:type="toc"]') || navDoc.querySelector('nav');
  if (!navElement) return toc;

  const mainList = navElement.querySelector('ol');
  if (!mainList) return toc;

  const processNavItem = (li, level = 0) => {
    const directA = li.querySelector(':scope > a');
    const nestedOl = Array.from(li.children).find(
      (child) => child.tagName === 'OL' || child.tagName === 'ol',
    );

    if (directA) {
      const href = directA.getAttribute('href');
      const label = directA.textContent.trim();
      if (label) toc.push({ label, href: href || undefined, level });
    } else if (nestedOl) {
      const labelFromLi = Array.from(li.childNodes)
        .filter(
          (n) =>
            n.nodeType === Node.TEXT_NODE ||
            (n.nodeType === Node.ELEMENT_NODE && n.tagName !== 'OL' && n.tagName !== 'ol'),
        )
        .map((n) => n.textContent?.trim() || '')
        .join(' ')
        .trim();
      if (labelFromLi) {
        const firstNestedHref = nestedOl.querySelector('a')?.getAttribute('href');
        toc.push({ label: labelFromLi, href: firstNestedHref || undefined, level });
      }
    }

    if (nestedOl) {
      Array.from(nestedOl.children)
        .filter((c) => c.tagName === 'LI' || c.tagName === 'li')
        .forEach((nestedLi) => processNavItem(nestedLi, level + 1));
    }
  };

  Array.from(mainList.children)
    .filter((c) => c.tagName === 'LI' || c.tagName === 'li')
    .forEach((li) => processNavItem(li, 0));

  return toc;
}

/** Parse EPUB 3 nav landmarks (cover, bodymatter, etc.). */
function parseNavLandmarks(navText) {
  const navDoc = parseXML(navText);
  const out = [];
  const landmarksNav = navDoc.querySelector('nav[epub\\:type="landmarks"]');
  if (!landmarksNav) return out;
  const list = landmarksNav.querySelector('ol, ul');
  if (!list) return out;
  Array.from(list.querySelectorAll('li a'))
    .slice(0, 20)
    .forEach((a) => {
      const href = a.getAttribute('href');
      const label = (a.textContent || '').trim();
      if (href && label) out.push({ label, href, level: 0 });
    });
  return out;
}

/** Parse EPUB 2 toc.ncx. */
function parseTOCNCX(tocText) {
  const tocDoc = parseXML(tocText);
  const toc = [];

  const processNavPoint = (navPoint, level = 0) => {
    const navLabel = navPoint.querySelector('navLabel > text');
    const content = navPoint.querySelector('content');
    if (!navLabel || !content) return;

    toc.push({
      label: navLabel.textContent.trim(),
      href: content.getAttribute('src'),
      level,
    });

    Array.from(navPoint.querySelectorAll('navPoint'))
      .filter((np) => np.parentElement === navPoint)
      .forEach((nestedPoint) => processNavPoint(nestedPoint, level + 1));
  };

  const navMap = tocDoc.querySelector('navMap');
  if (navMap) {
    Array.from(navMap.children)
      .filter((child) => child.tagName === 'navPoint')
      .forEach((navPoint) => processNavPoint(navPoint, 0));
  }
  return toc;
}

/**
 * Parse the TOC from an EPUB zip.
 * Tries: nav.xhtml (EPUB 3) → toc.ncx (EPUB 2) → OPF manifest nav property.
 */
export async function parseTOC(zip, opfPath) {
  const opfDir = opfPath.substring(0, opfPath.lastIndexOf('/') + 1);

  // 1) Try nav.xhtml at common locations
  const navPaths = [opfDir + 'nav.xhtml', 'nav.xhtml', 'OEBPS/nav.xhtml', 'OPS/nav.xhtml'];
  for (const navPath of navPaths) {
    try {
      const navFile = zip.file(navPath);
      if (!navFile) continue;
      const navText = await navFile.async('string');
      let toc = parseNavXHTML(navText);
      const landmarks = parseNavLandmarks(navText);
      const toPrepend = landmarks.filter((l) => !toc.some((t) => hrefMatches(t.href, l.href)));
      if (toPrepend.length) toc = [...toPrepend, ...toc];
      if (toc.length > 0) return toc;
    } catch {
      /* skip */
    }
  }

  // 2) Try toc.ncx at common locations
  const tocPaths = [opfDir + 'toc.ncx', 'toc.ncx', 'OEBPS/toc.ncx', 'OPS/toc.ncx'];
  for (const tocPath of tocPaths) {
    try {
      const tocFile = zip.file(tocPath);
      if (!tocFile) continue;
      const tocText = await tocFile.async('string');
      const toc = parseTOCNCX(tocText);
      if (toc.length > 0) return toc;
    } catch {
      /* skip */
    }
  }

  // 3) Try OPF manifest nav property
  try {
    const opfText = await zip.file(opfPath)?.async('string');
    if (opfText) {
      const opfDoc = parseXML(opfText);
      const navItems = opfDoc.querySelectorAll(
        'manifest > item[media-type="application/xhtml+xml"], manifest > item[media-type="text/html"]',
      );
      for (const item of navItems) {
        const href = item.getAttribute('href');
        const properties = item.getAttribute('properties');
        if (!properties?.includes('nav')) continue;
        const navPath = resolvePath(opfPath, href);
        const navText = await zip.file(navPath)?.async('string');
        if (!navText) continue;
        let toc = parseNavXHTML(navText);
        const landmarks = parseNavLandmarks(navText);
        const toPrepend = landmarks.filter((l) => !toc.some((t) => hrefMatches(t.href, l.href)));
        if (toPrepend.length) toc = [...toPrepend, ...toc];
        if (toc.length > 0) return toc;
      }
    }
  } catch {
    /* skip */
  }

  return [];
}
